/**
 * AdminAssetsScreen — biblioteca de assets premium subidos por Nilabs.
 *
 * Disponibles para todos los entrenadores en el picker de íconos del editor
 * (tab "Nilabs"). Diferencia el producto de Lucide/Phosphor (sets gratuitos
 * open-source) — el equipo Nilabs cura aquí íconos custom + ilustraciones
 * temáticas + logos colaboradores + packs estacionales.
 *
 * Flujo:
 *   - Filtros: tipo (icon/illustration), categoría, búsqueda
 *   - Click en "Subir asset" → modal con form (file picker + metadata)
 *   - Galería de cards (similar a AdminFoods/AdminExercises)
 *   - Click en card → modal edit (cambiar nombre, categoría, tags, swap archivo)
 *   - Eliminar con confirmación
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  adminCreateAsset,
  adminDeleteAsset,
  adminListAssets,
  adminUpdateAsset,
  adminUploadAssetFile,
  type AdminAsset,
  type AdminAssetInput,
  type AdminAssetType,
} from "../../api/admin";
import { Icon } from "../../lib/Icon";
import { EmptyState } from "../../lib/components";
import { useToast } from "../../lib/toast";
import { AdminBadge, AdminGate } from "./_shared";

const TYPE_LABELS: Record<AdminAssetType, string> = {
  icon: "Ícono",
  illustration: "Ilustración",
};

export function AdminAssetsScreen() {
  return (
    <AdminGate>
      <Content />
    </AdminGate>
  );
}

function Content() {
  const toast = useToast();
  const [assets, setAssets] = useState<AdminAsset[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [filterType, setFilterType] = useState<AdminAssetType | "">("");
  const [filterCat, setFilterCat] = useState<string>("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  /** `null` = no hay modal. `"new"` = crear. Objeto = editar. */
  const [editing, setEditing] = useState<AdminAsset | "new" | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const list = await adminListAssets({
        type: filterType || undefined,
        category: filterCat || undefined,
        query: debouncedQ || undefined,
      });
      setAssets(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [filterType, filterCat, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  // Set único de categorías presentes en los assets cargados — para el
  // dropdown de filtro. Si el admin agrega una categoría nueva, aparece
  // automáticamente tras reload.
  const allCategories = useMemo(() => {
    if (!assets) return [] as string[];
    const set = new Set<string>();
    for (const a of assets) if (a.category) set.add(a.category);
    return Array.from(set).sort();
  }, [assets]);

  const onSave = async (payload: AdminAssetInput, id?: number) => {
    try {
      if (id == null) {
        await adminCreateAsset(payload);
        toast(`${TYPE_LABELS[payload.type]} "${payload.name}" agregado`);
      } else {
        await adminUpdateAsset(id, payload);
        toast(`"${payload.name}" actualizado`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast("No pude guardar: " + (e instanceof Error ? e.message : ""));
    }
  };

  const onDelete = async (a: AdminAsset) => {
    if (!confirm(`¿Eliminar "${a.name}" de la biblioteca? Los planes que lo usen como decoración verán un espacio vacío.`)) return;
    try {
      await adminDeleteAsset(a.id);
      toast(`"${a.name}" eliminado`);
      await load();
    } catch (e) {
      toast("No pude eliminar: " + (e instanceof Error ? e.message : ""));
    }
  };

  return (
    <div className="page al-admin">
      <header className="al-admin-header">
        <div>
          <h1>
            Biblioteca de assets <AdminBadge />
          </h1>
          <p className="muted">
            Íconos e ilustraciones premium curados por Nilabs. Aparecen en el
            picker del editor en la sección <b>Nilabs</b> — mezclados con
            Lucide y Phosphor pero diferenciados.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing("new")}>
          <Icon name="plus" size={14} /> Subir asset
        </button>
      </header>

      <div className="al-admin-foods-toolbar">
        <div className="al-admin-foods-search">
          <Icon name="search" size={14} />
          <input
            type="text"
            placeholder="Buscar por nombre o tags…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && <button className="link-btn" onClick={() => setQ("")}>limpiar</button>}
        </div>
        <select
          className="al-admin-foods-cat"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as AdminAssetType | "")}
        >
          <option value="">Todos los tipos</option>
          <option value="icon">Íconos</option>
          <option value="illustration">Ilustraciones</option>
        </select>
        <select
          className="al-admin-foods-cat"
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          {allCategories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <div className="tiny muted al-admin-foods-count">
          {assets ? `${assets.length} assets` : "—"}
        </div>
      </div>

      {err && (
        <EmptyState
          icon="x"
          title="Error al cargar la biblioteca"
          body={err}
          action={<button className="btn btn-secondary" onClick={load}>Reintentar</button>}
        />
      )}

      {!err && loading && !assets && (
        <div className="muted" style={{ padding: 24 }}>Cargando assets…</div>
      )}

      {!err && assets && assets.length === 0 && (
        <EmptyState
          icon="gift"
          title="Sin assets todavía"
          body={debouncedQ || filterType || filterCat
            ? "Probá con otros filtros o limpiá la búsqueda."
            : "Subí el primer asset para que aparezca en el picker de los coaches."}
          action={<button className="btn btn-primary" onClick={() => setEditing("new")}>
            <Icon name="plus" size={14} /> Subir primer asset
          </button>}
        />
      )}

      {!err && assets && assets.length > 0 && (
        <div className="al-admin-assets-grid">
          {assets.map((a) => (
            <AssetCard key={a.id} asset={a} onEdit={() => setEditing(a)} onDelete={() => onDelete(a)} />
          ))}
        </div>
      )}

      {editing && (
        <AssetForm
          asset={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// AssetCard — preview del asset + nombre + acciones
// ----------------------------------------------------------------------

function AssetCard({
  asset, onEdit, onDelete,
}: {
  asset: AdminAsset;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="al-admin-asset-card">
      <button
        type="button"
        className="al-admin-asset-preview"
        onClick={onEdit}
        title="Editar"
      >
        <img src={asset.url} alt={asset.name} loading="lazy" />
      </button>
      <div className="al-admin-asset-meta">
        <div className="al-admin-asset-name">{asset.name}</div>
        <div className="tiny muted">
          <span className="al-admin-asset-type-tag">{TYPE_LABELS[asset.type]}</span>
          {asset.category ? <> · {asset.category}</> : null}
        </div>
        {asset.tags && (
          <div className="tiny muted al-admin-asset-tags" title={asset.tags}>
            {asset.tags}
          </div>
        )}
      </div>
      <div className="al-admin-asset-actions">
        <button className="btn btn-ghost btn-sm" onClick={onEdit}>
          <Icon name="settings" size={11} />
        </button>
        <button
          className="btn btn-ghost btn-sm al-admin-foods-del"
          onClick={onDelete}
          title="Eliminar"
        >
          <Icon name="x" size={11} />
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// AssetForm — modal subir/editar asset
// ----------------------------------------------------------------------

interface AssetFormProps {
  asset: AdminAsset | null;
  onClose: () => void;
  onSave: (payload: AdminAssetInput, id?: number) => Promise<void>;
}

function AssetForm({ asset, onClose, onSave }: AssetFormProps) {
  const toast = useToast();
  const [type, setType] = useState<AdminAssetType>(asset?.type || "icon");
  const [name, setName] = useState(asset?.name || "");
  const [category, setCategory] = useState(asset?.category || "");
  const [tags, setTags] = useState(asset?.tags || "");
  const [url, setUrl] = useState(asset?.url || "");
  const [mime, setMime] = useState(asset?.mime || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !uploading && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, uploading, saving]);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const res = await adminUploadAssetFile(file);
      setUrl(res.url);
      setMime(res.mime);
      // Si el nombre está vacío, sugerimos el del archivo (sin extensión).
      if (!name.trim()) {
        setName(file.name.replace(/\.[^.]+$/, ""));
      }
      toast(`Archivo subido (${res.file_size_kb} kB)`);
    } catch (e) {
      toast("Error al subir: " + (e instanceof Error ? e.message : ""));
    } finally {
      setUploading(false);
    }
  };

  const canSave = name.trim().length > 0 && url.trim().length > 0 && mime.length > 0;

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await onSave(
        {
          type,
          name: name.trim(),
          category: category.trim() || null,
          tags: tags.trim() || null,
          url: url.trim(),
          mime,
        },
        asset?.id,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="al-modal-backdrop" onClick={() => !uploading && !saving && onClose()}>
      <div
        className="al-modal al-admin-asset-form"
        role="dialog"
        aria-label="Asset"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
            {asset ? `Editar "${asset.name}"` : "Subir nuevo asset"}
          </h3>
          <button className="icon-btn" onClick={onClose} title="Cerrar" disabled={uploading || saving}>
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="al-admin-asset-form-body">
          {/* Preview + uploader */}
          <div className="al-admin-asset-form-preview">
            {url ? (
              <img src={url} alt="" />
            ) : (
              <div className="al-admin-asset-form-preview-empty">
                <Icon name="photo" size={36} />
                <span className="tiny">Sin archivo</span>
              </div>
            )}
          </div>

          <label className="al-food-custom-field">
            <span className="tiny" style={{ fontWeight: 600 }}>Archivo (SVG / PNG / WebP, máx 4 MB)</span>
            <input
              type="file"
              accept="image/svg+xml,image/png,image/webp,image/jpeg"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
                e.currentTarget.value = "";
              }}
              disabled={uploading || saving}
            />
            {uploading && <span className="tiny muted">Subiendo…</span>}
          </label>

          <div className="al-admin-foods-form-grid">
            <label className="al-food-custom-field">
              <span className="tiny" style={{ fontWeight: 600 }}>Tipo *</span>
              <select value={type} onChange={(e) => setType(e.target.value as AdminAssetType)} disabled={saving}>
                <option value="icon">Ícono</option>
                <option value="illustration">Ilustración</option>
              </select>
            </label>
            <label className="al-food-custom-field">
              <span className="tiny" style={{ fontWeight: 600 }}>Categoría</span>
              <input
                type="text"
                className="al-input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Fitness, Cliente, Logos…"
                disabled={saving}
              />
            </label>
          </div>

          <label className="al-food-custom-field">
            <span className="tiny" style={{ fontWeight: 600 }}>Nombre *</span>
            <input
              type="text"
              className="al-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Mancuerna Premium, Logo Megaplex"
              disabled={saving}
              autoFocus
            />
          </label>

          <label className="al-food-custom-field">
            <span className="tiny" style={{ fontWeight: 600 }}>
              Tags <span className="muted">(separados por coma — usados en búsqueda)</span>
            </span>
            <input
              type="text"
              className="al-input"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="fitness, mancuerna, peso, gym"
              disabled={saving}
            />
          </label>

          <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={uploading || saving}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={submit}
              disabled={!canSave || saving || uploading}
            >
              {saving ? "Guardando…" : asset ? "Guardar cambios" : "Subir asset"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
